/**
 * The integration registry — the declaration layer. One entry per
 * operator-configurable integration: `secrets` declares the operator-entered
 * credential fields — each a flat string schema whose `.describe()` states
 * that field's meaning. One statement types the stored value and validates
 * the save input — no drift between the field an operator fills and the field
 * the platform stores. What an integration is CALLED is copy, read in the
 * operator's language: the admin catalog keys each id's title
 * (`admin.integrations.titles.<id>`), because this file has no locale axis.
 *
 * This file imports zod and nothing else. The registry is data — no
 * provider SDK, no Firestore, no KMS — so the declaration layer lifts to
 * another project verbatim. `Verify<Id>` is the signature a provider's own
 * verification satisfies: it receives the declared secrets in plaintext,
 * proves the credential by using it, and throws on failure — an
 * implementation lives beside its provider's client in the functions
 * workspace, never here.
 *
 * Entries justified:
 *   lettermint       — projectToken (lm_…, the sending credential every
 *                      outbound email authenticates with) and teamToken
 *                      (lm_team_…, the Team API credential; never used to
 *                      send).
 *   revolutSandbox / — one id per Revolut environment, each holding that
 *   revolutLive        environment's charge-capable sk_ key. Two ids
 *                      rather than one id with an env field: the money
 *                      path resolves a credential BY environment — an
 *                      order stays pinned to the environment it was
 *                      authorised in, whatever mode is active for new
 *                      checkouts — and an operator holds both
 *                      environments' keys at once, saving each
 *                      independently. Two ids carry that as two documents;
 *                      an env field inside a flat one-string-per-field
 *                      shape could hold only one environment's key at a
 *                      time.
 *   appleWallet      — certificatePem (the Pass Type ID certificate,
 *                      PEM-encoded, bundled with its own private key — the
 *                      credential every minted pass signs with) and
 *                      wwdrPem (the WWDR G4 intermediate it chains to).
 *   googleWallet     — no secrets: the runtime service account
 *                      authenticates as itself. The entry exists so the
 *                      panel row, the status block, and the verification
 *                      derive the same way as every other id's.
 *   gemini           — apiKey (the Google AI Studio key every Gemini call
 *                      authenticates with — event artwork generation,
 *                      legal-document formatting and review). The operator's
 *                      own key; every consumer today resolves the
 *                      deployment's GEMINI_API_KEY secret binding, the only
 *                      path until a consumer reads the stored credential.
 *   metaAds          — accessToken (the Conversions API token every
 *                      server-side conversion POST and its verification GET
 *                      authenticate with — a System User token the operator
 *                      generates in Events Manager) and appSecret (the
 *                      client's own Meta app secret). Both are declared here
 *                      because they share one blast radius: `appsecret_proof`
 *                      is an HMAC of the access token keyed by the app
 *                      secret, so whoever holds the secret can sign for the
 *                      token, and a rotation of either is a rotation of the
 *                      pair's authority. The app SECRET is the credential;
 *                      the app ID beside it is not, and travels on the save
 *                      as an operational field the way the Pixel ID already
 *                      does.
 *
 *                      The Pixel ID, the app id, the ad account, the page and
 *                      the Instagram account are all deliberately NOT secrets
 *                      here: each is a public identifier of a Meta object,
 *                      each travels on the save as an operational field, and
 *                      each is paired with this token by the register hook —
 *                      never a credential.
 *
 * Excluded:
 *   signingSecret / signingSecrets (Lettermint, Revolut) — provider-issued
 *   at webhook registration and written by that registration seam, never
 *   typed by an operator, so neither is a declared credential and neither
 *   renders a form field; `signingSecrets` is additionally a
 *   per-registration map keyed by provider webhook id, not a flat string.
 *   Google Wallet's issuer id — configuration, not a credential; it lives
 *   on the member-readable status block.
 */
import { z } from "zod";

type Declaration = {
	secrets: Record<string, z.ZodString>;
};

// A credential that travels in an HTTP header. An interior control character
// (a multi-line clipboard paste) makes fetch throw client-side before any
// network I/O, with the full value verbatim in the TypeError — rejecting it
// at the parse keeps the value out of every error string. Not for the PEM
// fields: PEMs are multi-line by nature and never travel in a header.
const headerSafe = z
	.string()
	.min(1)
	.regex(/^[\x21-\x7E]+$/, "No spaces or line breaks — paste it as one line.");

export const REGISTRY = {
	lettermint: {
		secrets: {
			projectToken: headerSafe.describe("Project API token"),
			teamToken: headerSafe.describe("Team API token"),
		},
	},
	revolutSandbox: {
		secrets: {
			secretKey: headerSafe.describe("Merchant API secret key"),
		},
	},
	revolutLive: {
		secrets: {
			secretKey: headerSafe.describe("Merchant API secret key"),
		},
	},
	appleWallet: {
		secrets: {
			certificatePem: z
				.string()
				.min(1)
				.describe("Pass Type ID certificate (PEM, with private key)"),
			wwdrPem: z
				.string()
				.min(1)
				.describe("Apple WWDR G4 intermediate certificate (PEM)"),
		},
	},
	googleWallet: {
		secrets: {},
	},
	gemini: {
		secrets: {
			apiKey: headerSafe.describe("Gemini API key"),
		},
	},
	metaAds: {
		secrets: {
			accessToken: headerSafe.describe("Conversions API access token"),
			// headerSafe for its bad-paste guard, not its transport claim: this
			// one keys an HMAC locally and never leaves as a header value.
			appSecret: headerSafe.describe("App secret"),
		},
	},
} as const satisfies Record<string, Declaration>;

export type IntegrationId = keyof typeof REGISTRY;

/**
 * What an id's credential resolves to for the provider that proves it. Every
 * declared field carries a value except one the registry declared after this
 * credential was already stored, which stays absent until a save supplies it
 * — so a provider reading that field can be handed nothing.
 *
 * Every field is `string` rather than optional, and the asymmetry is the
 * reason: an optional type flags the providers whose fields predate every
 * stored credential and therefore cannot be missing, while the shape that
 * actually loses an absent secret — interpolating it into a header, a URL or
 * a signature — takes `undefined` silently and is flagged by nothing. So the
 * obligation sits with the provider, not the type: one that cannot proceed
 * without a particular field tests that field and says which is missing,
 * rather than passing it on to be read as the text "undefined" and answered
 * as a rejected credential.
 */
export type SecretsOf<Id extends IntegrationId> = {
	[K in keyof (typeof REGISTRY)[Id]["secrets"]]: string;
};

/**
 * Proves the credential by using it — throws ⇒ failure.
 *
 * Resolved secrets and nothing else. A variant's own operational fields
 * (saveIntegration.action.ts) reach the provider through its `register` hook,
 * which runs once the proof holds and receives that save's raw input; the save
 * contract imports this file, so the type of that input cannot be named here
 * without a cycle, and the proof stays over the credential alone.
 *
 * The consequence, named where a reader meets it rather than left to be
 * rediscovered: anything a verify consults beyond its secrets it reads from
 * the store, so it sees what the LAST save wrote, and a save that first states
 * an operational field is proved against what stood before it. The credential
 * is never stale — it is in hand. A check that must bind on the save that
 * states its own input belongs in `register` instead, which holds that input,
 * and whose throw resolves the same `ok: false` outcome under the same
 * classified reason.
 */
export type Verify<Id extends IntegrationId> = (
	orgId: string,
	secrets: SecretsOf<Id>,
) => Promise<unknown>;
